import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ManagerLayout from "../../layouts/ManagerLayout";
import { motion, AnimatePresence } from "framer-motion";
import { createProject, getProjects, getProjectLocations, createProjectLocation } from "../../api/projectService";


function CreateProjectLocationModal({ onClose, onCreated }) {
  const [name, setName]       = useState("");
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { setError("Location name is required."); return; }
    try {
      setLoading(true);
      const res = await createProjectLocation({ name: name.trim() });
      onCreated(res.data);
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        "Failed to create location."
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.88, opacity: 0, y: 24 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.88, opacity: 0, y: 24 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        className="bg-gray-900/98 border border-white/15 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 sm:px-7 pt-5 sm:pt-7 pb-4 sm:pb-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center shadow-lg text-lg sm:text-xl flex-shrink-0">
              📍
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white">Add Project Location</h2>
              <p className="text-white/45 text-xs">Create a new location for projects</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-all text-sm flex-shrink-0"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 sm:px-7 py-5 sm:py-6 space-y-4 sm:space-y-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-white/75 font-semibold text-xs uppercase tracking-wide">
              Location Name <span className="text-red-400">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => { setName(e.target.value); setError(""); }}
              placeholder="e.g. Hyderabad Office"
              className={`w-full bg-white/10 border rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors placeholder:text-white/30 ${
                error
                  ? "border-red-400/60 focus:border-red-400"
                  : "border-white/30 hover:border-white/50 focus:border-violet-400"
              }`}
            />
            {error && <p className="text-red-400 text-xs">{error}</p>}
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-bold text-sm shadow-lg transition-all disabled:opacity-55"
            >
              {loading ? "Creating…" : "Create Location"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}


function LocationSelectWithAdd({ value, onChange, locations, onAddClick }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="block text-white font-semibold text-sm">Project Location</label>
        <button
          type="button"
          onClick={onAddClick}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-500/20 hover:bg-violet-500/35 border border-violet-400/30 hover:border-violet-400/55 text-violet-300 hover:text-white text-xs font-semibold transition-all"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          Add Location
        </button>
      </div>
      <select
        name="location_id"
        value={value}
        onChange={onChange}
        className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-violet-400"
      >
        <option value="" className="bg-gray-900">-- Select Location (optional) --</option>
        {locations.map((l) => (
          <option key={l.id} value={l.id} className="bg-gray-900">
            {l.name}
          </option>
        ))}
      </select>
    </div>
  );
}


function CreateProject() {
  const [form, setForm] = useState({
    project_name: "",
    project_description: "",
    location_id: "",
  });
  const [projectIdSuffix, setProjectIdSuffix]     = useState("");
  const [latestProjectId, setLatestProjectId]     = useState("");
  const [loading, setLoading]                     = useState(false);
  const [successMsg, setSuccessMsg]               = useState("");
  const [errorMsg, setErrorMsg]                   = useState("");
  const [locations, setLocations]                 = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);

  useEffect(() => {
    getProjects()
      .then((res) => {
        const list = res.data || [];
        if (Array.isArray(list) && list.length > 0) {
          const sorted = [...list].sort((a, b) => new Date(b.created_date || 0) - new Date(a.created_date || 0));
          setLatestProjectId(sorted[0]?.project_id || "");
        }
      })
      .catch(() => {});

    getProjectLocations()
      .then((res) => setLocations(res.data.locations || []))
      .catch(() => {});
  }, []);

  async function handleLocationCreated(data) {
    try {
      const res = await getProjectLocations();
      setLocations(res.data.locations || []);
      setForm((prev) => ({ ...prev, location_id: String(data.id) }));
    } catch { /* silent */ }
  }

  function handleProjectIdChange(e) {
    let val = e.target.value;
    val = val.replace(/^PROJ-/i, "");
    setProjectIdSuffix(val);
  }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  const validateForm = () => {
    if (!projectIdSuffix.trim()) return "Project ID suffix is required (e.g. 001, 002, 345, 654)";
    if (!form.project_name.trim()) return "Project name is required";
    if (!form.project_description.trim()) return "Project description is required";
    return null;
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");
    const validationError = validateForm();
    if (validationError) { setErrorMsg(validationError); return; }
    setLoading(true);
    try {
      const fullProjectId = `PROJ-${projectIdSuffix.trim().toUpperCase()}`;
      const payload = {
        project_id: fullProjectId,
        project_name: form.project_name.trim(),
        project_description: form.project_description.trim(),
        ...(form.location_id && { location_id: parseInt(form.location_id) }),
      };
      const res = await createProject(payload);
      if (res.data?.success) {
        setSuccessMsg(res.data.message || "Project created successfully ✅");
        setLatestProjectId(fullProjectId);
        setProjectIdSuffix("");
        setForm({ project_name: "", project_description: "", location_id: "" });
      } else {
        setErrorMsg("Unexpected response from server.");
      }
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to create project."
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <ManagerLayout>
      <AnimatePresence>
        {showLocationModal && (
          <CreateProjectLocationModal
            key="proj-loc-modal"
            onClose={() => setShowLocationModal(false)}
            onCreated={handleLocationCreated}
          />
        )}
      </AnimatePresence>

      <div className="p-3 sm:p-6">
        <div className="w-full max-w-[1400px] mx-auto">

          {/* HEADER */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="max-w-3xl mx-auto mb-6 sm:mb-8"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center shadow-lg flex-shrink-0">
                  <span className="text-2xl sm:text-3xl">📁</span>
                </div>
                <div>
                  <h1 className="text-xl sm:text-3xl font-bold text-white">Create New Project</h1>
                  <p className="text-white/60 text-xs sm:text-sm">Initialize a new project with team and timeline</p>
                </div>
              </div>

              <Link to="/manager/projects/bulk-create" className="w-full sm:w-auto">
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border-2 border-violet-400/60 bg-violet-500/15 text-violet-200 font-semibold text-sm backdrop-blur-sm hover:bg-violet-500/25 hover:border-violet-300 transition-all shadow-lg"
                >
                  <span className="text-base">📊</span>
                  Bulk Upload
                </motion.button>
              </Link>
            </div>
          </motion.div>

          {/* FORM */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-white/15 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-4 sm:p-8 max-w-3xl mx-auto"
          >
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-white font-semibold text-sm">Project ID</label>
                  {latestProjectId && (
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-violet-500/20 text-violet-300 border border-violet-400/30 flex items-center gap-1.5 shadow-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse"></span>
                      Latest: <strong className="text-white">{latestProjectId}</strong>
                    </span>
                  )}
                </div>
                <div className="flex items-center w-full bg-white/10 border border-white/30 rounded-xl overflow-hidden focus-within:border-violet-400 transition-colors">
                  <span className="px-4 py-3 bg-white/15 text-violet-300 font-bold text-sm border-r border-white/20 select-none flex-shrink-0">
                    PROJ-
                  </span>
                  <input
                    type="text"
                    name="project_id_suffix"
                    value={projectIdSuffix}
                    onChange={handleProjectIdChange}
                    required
                    placeholder="Enter the ID after latest Project ID shown above"
                    className="w-full bg-transparent px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none"
                  />
                </div>
                <p className="text-white/40 text-xs mt-1.5">
                  Enter the ID after latest Project ID shown above. Project ID: <span className="text-violet-300 font-mono font-semibold">PROJ-{projectIdSuffix.trim() || "..."}</span>
                </p>
              </div>

              <div>
                <label className="block text-white font-semibold mb-2 text-sm">Project Name</label>
                <input
                  type="text"
                  name="project_name"
                  value={form.project_name}
                  onChange={handleChange}
                  required
                  placeholder="Enter project name"
                  className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white text-sm placeholder-white/40 focus:outline-none focus:border-violet-400"
                />
              </div>

              <div>
                <label className="block text-white font-semibold mb-2 text-sm">Project Description</label>
                <textarea
                  name="project_description"
                  value={form.project_description}
                  onChange={handleChange}
                  rows="4"
                  required
                  placeholder="Enter project description..."
                  className="w-full bg-white/10 border border-white/30 rounded-xl px-4 py-3 text-white text-sm placeholder-white/40 resize-none focus:outline-none focus:border-violet-400"
                />
              </div>

              <LocationSelectWithAdd
                value={form.location_id}
                onChange={handleChange}
                locations={locations}
                onAddClick={() => setShowLocationModal(true)}
              />

              {successMsg && <p className="text-green-300 font-semibold text-sm">{successMsg}</p>}
              {errorMsg   && <p className="text-red-300 font-semibold text-sm">{errorMsg}</p>}

              <div className="flex justify-center pt-2 sm:pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-8 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 text-white font-bold disabled:opacity-60 hover:scale-105 transition-all text-sm"
                >
                  {loading ? "Creating..." : "Create Project"}
                </button>
              </div>

            </form>
          </motion.div>

        </div>
      </div>
    </ManagerLayout>
  );
}

export default CreateProject;